import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  CreditCard, 
  Users, 
  TrendingUp, 
  Clock, 
  Plus,
  ArrowRight,
  CheckCircle,
  AlertCircle,
  Wallet
} from "lucide-react";
import { formatCurrency } from "@/lib/currency";
import { useMemo } from "react";
import { ProfessionalTerminology } from "./ProfessionalSidebar";


interface Participant {
  id: string;
  name: string;
  email: string;
  has_paid: boolean;
}

interface SplitPaymentRequest {
  id: string;
  product_name: string;
  total_price: number;
  status: string;
  created_at: string;
  deadline: string;
  participants: Participant[];
}

interface SubscriptionInfo {
  plan: string;
  status: string;
  daysRemaining?: number;
}

interface MerchantInfo {
  id: string;
  store_name: string;
  currency?: string;
}

interface ProfessionalOverviewProps {
  requests: SplitPaymentRequest[];
  merchantInfo: MerchantInfo | null;
  subscription: SubscriptionInfo | null;
  terminology?: ProfessionalTerminology;
  onNavigateToTab: (tab: string, subView?: string) => void;
  onCreateCollection?: () => void;
}

// NOTE: Using function declaration instead of const arrow to prevent TDZ errors in production builds
export function ProfessionalOverview({
  requests,
  merchantInfo,
  subscription,
  terminology = {},
  onNavigateToTab,
  onCreateCollection,
}: ProfessionalOverviewProps) {
  const currency = (merchantInfo?.currency as any) || 'INR';

  // Labels based on terminology
  const labels = {
    payments: terminology.orders || "Payments",
    clients: terminology.customers || "Clients",
    business: terminology.store || "Business",
  };

  // Calculate stats
  const stats = useMemo(() => {
    const now = new Date();
    const oneWeekFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
    
    // Total Earnings - all completed collections
    const totalEarnings = requests
      .filter(r => r.status === 'completed')
      .reduce((sum, r) => sum + r.total_price, 0);

    // Upcoming This Week - pending payments due in next 7 days
    const upcomingThisWeek = requests
      .filter(r => {
        if (r.status !== 'pending') return false;
        const deadline = new Date(r.deadline);
        return deadline >= now && deadline <= oneWeekFromNow;
      })
      .reduce((sum, r) => {
        const unpaidParticipants = r.participants.filter(p => !p.has_paid).length;
        const totalParticipants = r.participants.length;
        const unpaidRatio = totalParticipants > 0 ? unpaidParticipants / totalParticipants : 0;
        return sum + (r.total_price * unpaidRatio);
      }, 0);

    // Active Clients - unique clients with active (pending) collections
    const activeClientEmails = new Set<string>();
    requests
      .filter(r => r.status === 'pending')
      .forEach(r => {
        r.participants.forEach(p => activeClientEmails.add(p.email.toLowerCase()));
      });

    // Overdue Amount - pending payments past deadline
    const overdueAmount = requests
      .filter(r => {
        if (r.status !== 'pending') return false;
        const deadline = new Date(r.deadline);
        return deadline < now;
      })
      .reduce((sum, r) => {
        const unpaidParticipants = r.participants.filter(p => !p.has_paid).length;
        const totalParticipants = r.participants.length;
        const unpaidRatio = totalParticipants > 0 ? unpaidParticipants / totalParticipants : 0;
        return sum + (r.total_price * unpaidRatio);
      }, 0);

    return {
      totalEarnings,
      upcomingThisWeek,
      activeClients: activeClientEmails.size,
      overdueAmount,
    };
  }, [requests]);

  // Recent pending collections
  const recentPending = useMemo(() => {
    return requests
      .filter(r => r.status === 'pending')
      .slice(0, 3);
  }, [requests]);

  return (
    <div className="space-y-6">
      {/* Welcome Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Welcome back!</h1>
          <p className="text-muted-foreground">
            Here's what's happening with your {labels.payments.toLowerCase()} today
          </p>
        </div>
        <Button 
          onClick={onCreateCollection}
          className="bg-cyan-600 hover:bg-cyan-700 gap-2"
        >
          <Plus className="h-4 w-4" />
          New Collection
        </Button>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-emerald-500">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Earnings</p>
                <p className="text-2xl font-bold">{formatCurrency(stats.totalEarnings, currency)}</p>
              </div>
              <div className="p-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg">
                <Wallet className="h-5 w-5 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-cyan-500">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Upcoming This Week</p>
                <p className="text-2xl font-bold">{formatCurrency(stats.upcomingThisWeek, currency)}</p>
              </div>
              <div className="p-2 bg-cyan-100 dark:bg-cyan-900/30 rounded-lg">
                <Clock className="h-5 w-5 text-cyan-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className={`border-l-4 ${stats.overdueAmount > 0 ? 'border-l-red-500' : 'border-l-gray-300'}`}>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Overdue Amount</p>
                <p className={`text-2xl font-bold ${stats.overdueAmount > 0 ? 'text-red-600' : ''}`}>
                  {formatCurrency(stats.overdueAmount, currency)}
                </p>
              </div>
              <div className={`p-2 rounded-lg ${stats.overdueAmount > 0 ? 'bg-red-100 dark:bg-red-900/30' : 'bg-gray-100 dark:bg-gray-800'}`}>
                <AlertCircle className={`h-5 w-5 ${stats.overdueAmount > 0 ? 'text-red-600' : 'text-gray-400'}`} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-cyan-500">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Active {labels.clients}</p>
                <p className="text-2xl font-bold">{stats.activeClients}</p>
              </div>
              <div className="p-2 bg-cyan-100 dark:bg-cyan-900/30 rounded-lg">
                <Users className="h-5 w-5 text-cyan-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Active Collections */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle className="text-lg">Active Collections</CardTitle>
            <CardDescription>Your ongoing payment collections</CardDescription>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => onNavigateToTab('payments', 'collections')}
            className="gap-1"
          >
            View all <ArrowRight className="h-4 w-4" />
          </Button>
        </CardHeader>
        <CardContent>
          {recentPending.length === 0 ? (
            <div className="text-center py-8">
              <CreditCard className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground mb-4">No active collections</p>
              <Button 
                onClick={onCreateCollection}
                variant="outline"
                className="gap-2"
              >
                <Plus className="h-4 w-4" />
                Create your first collection
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {recentPending.map((request) => {
                const paidCount = request.participants.filter(p => p.has_paid).length;
                const totalCount = request.participants.length;
                const progress = totalCount > 0 ? (paidCount / totalCount) * 100 : 0;
                const deadline = new Date(request.deadline);
                const isNearDeadline = deadline.getTime() - Date.now() < 24 * 60 * 60 * 1000;

                return (
                  <div 
                    key={request.id}
                    className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors cursor-pointer"
                    onClick={() => window.open(`/split/${request.id}`, '_blank')}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{request.product_name}</p>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <span>{paidCount}/{totalCount} paid</span>
                        <span>•</span>
                        <span>{formatCurrency(request.total_price, currency)}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      <div className="w-24 h-2 bg-muted rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-cyan-500 transition-all"
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                      {isNearDeadline && (
                        <Badge variant="outline" className="text-amber-600 border-amber-300">
                          <Clock className="h-3 w-3 mr-1" />
                          Soon
                        </Badge>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card 
          className="hover:border-cyan-300 dark:hover:border-cyan-700 transition-colors cursor-pointer group"
          onClick={() => onNavigateToTab('reports', 'analytics')}
        >
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-cyan-100 dark:bg-cyan-900/30 rounded-lg group-hover:bg-cyan-200 dark:group-hover:bg-cyan-900/50 transition-colors">
                <TrendingUp className="h-5 w-5 text-cyan-600" />
              </div>
              <div>
                <p className="font-medium">View Analytics</p>
                <p className="text-sm text-muted-foreground">Track your performance</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="hover:border-cyan-300 dark:hover:border-cyan-700 transition-colors cursor-pointer group"
          onClick={() => onNavigateToTab('services', 'clients')}
        >
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-cyan-100 dark:bg-cyan-900/30 rounded-lg group-hover:bg-cyan-200 dark:group-hover:bg-cyan-900/50 transition-colors">
                <Users className="h-5 w-5 text-cyan-600" />
              </div>
              <div>
                <p className="font-medium">Manage {labels.clients}</p>
                <p className="text-sm text-muted-foreground">View client history</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="hover:border-cyan-300 dark:hover:border-cyan-700 transition-colors cursor-pointer group"
          onClick={() => onNavigateToTab('profile', 'bank')}
        >
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-cyan-100 dark:bg-cyan-900/30 rounded-lg group-hover:bg-cyan-200 dark:group-hover:bg-cyan-900/50 transition-colors">
                <Wallet className="h-5 w-5 text-cyan-600" />
              </div>
              <div>
                <p className="font-medium">Your Bank</p>
                <p className="text-sm text-muted-foreground">Manage bank details</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

    </div>
  );
};
